import {Icon, NewButton, Pagination, Search} from 'letrus-ui';
import {Link, useHistory} from 'react-router-dom';
import {ChangeEvent, useEffect, useMemo, useState} from 'react';
import NoDataIllustration from 'images/no-data.svg';
import LoadingTable from 'components/LoadingComponents/LoadingTable';
import DeletionModal from 'components/DeletionModal';
import Select from 'react-select';
import {
  useDeleteGrammaRule,
  useFetchGrammarRulesLazy,
} from 'store/reducers/grammarRules';
import FeedbackModal from 'components/FeedbackModal';
import entitiesPerPageOptions from 'utils/entitiesPerPageOptions';
import SelectOption from 'utils/types/SelectOption';
import {AuthRoutes} from 'routes';
import PageWrapper from '../../components/PageWrapper';
import styles from './GrammarRuleList.module.scss';

const GrammarRuleList: React.FC = () => {
  const [searchText, setSearchText] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [numberOfGrammarRulesPerPage, setNumberOfGrammarRulesPerPage] =
    useState<SelectOption | null>({label: '10', value: '10'});
  const [isDeleteGrammarRuleModalOpen, setIsDeleteGrammarRuleModalOpen] =
    useState<boolean>(false);
  const [grammarRuleToDeleteId, setGrammarRuleToDeleteId] = useState<number>(0);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] =
    useState<boolean>(false);

  const [
    fetchGrammarRulesLazy,
    {
      isFetching: isFetchingGrammarRules,
      isLoading: isLoadingGrammarRules,
      isUninitialized: isFetchGrammarRulesUninitialized,
      data,
    },
  ] = useFetchGrammarRulesLazy();
  const [
    deleteGrammarRule,
    {
      isLoading: isDeletingGrammarRule,
      isSuccess: isDeleteSuccess,
      isError: isDeleteError,
    },
  ] = useDeleteGrammaRule();

  const history = useHistory();

  useEffect(() => {
    fetchGrammarRulesLazy(
      {
        search: searchText,
        limit: numberOfGrammarRulesPerPage?.value,
        offset: (currentPage - 1) * Number(numberOfGrammarRulesPerPage?.value),
      },
      true,
    );
  }, [currentPage, numberOfGrammarRulesPerPage?.value]);

  useEffect(() => {
    if (isDeleteError) {
      setIsFeedbackModalOpen(true);
    }
  }, [isDeleteError]);

  useEffect(() => {
    if (isDeleteSuccess) {
      setIsFeedbackModalOpen(true);
    }
  }, [isDeleteSuccess]);

  useEffect(() => {
    if (isDeleteSuccess && !isDeletingGrammarRule) {
      fetchGrammarRulesLazy({}, true);
    }
  }, [isDeleteSuccess, isDeletingGrammarRule]);

  const totalNumberOfPages = useMemo(
    () =>
      data?.total
        ? Math.ceil(data.total / Number(numberOfGrammarRulesPerPage?.value))
        : 0,
    [data],
  );
  const isLoading = isLoadingGrammarRules || isFetchingGrammarRules;
  const hasFirstGrammarRulesFetchHappened = !isFetchGrammarRulesUninitialized;

  function onChangeSearchText(event: ChangeEvent<HTMLInputElement>) {
    if (!event.target.value || event.target.value === '') {
      fetchGrammarRulesLazy(
        {limit: numberOfGrammarRulesPerPage?.value, offset: 0},
        true,
      );

      setCurrentPage(1);
    }

    const inputValue = event.target.value.toLowerCase();

    setSearchText(inputValue);
  }

  function onClickDeleteGrammarRule(grammarRuleId: number) {
    setGrammarRuleToDeleteId(grammarRuleId);
    setIsDeleteGrammarRuleModalOpen(true);
  }

  function onDeleteGrammarRule() {
    deleteGrammarRule({grammarRuleId: grammarRuleToDeleteId});
    setIsDeleteGrammarRuleModalOpen(false);
  }

  function onSubmitSearch() {
    setCurrentPage(1);

    fetchGrammarRulesLazy(
      {
        search: searchText,
        limit: numberOfGrammarRulesPerPage?.value,
        offset: (currentPage - 1) * Number(numberOfGrammarRulesPerPage?.value),
      },
      true,
    );
  }

  function resetData() {
    setCurrentPage(1);
    setSearchText('');

    fetchGrammarRulesLazy(
      {
        limit: numberOfGrammarRulesPerPage?.value,
        offset: (currentPage - 1) * Number(numberOfGrammarRulesPerPage?.value),
      },
      true,
    );
  }

  return (
    <PageWrapper>
      <div className={styles.container}>
        <header>
          <h1>Regras gramaticais</h1>

          <NewButton
            text="Criar regra"
            kind="primary"
            userRole="teacher"
            type="button"
            onClick={() => history.push(AuthRoutes.grammarRuleCreation)}
          />
        </header>

        {(isLoading || isDeletingGrammarRule) && (
          <div className={styles.loadingWrapper}>
            <LoadingTable />
          </div>
        )}

        {!isLoading &&
          hasFirstGrammarRulesFetchHappened &&
          !data?.results?.length && (
            <div className={styles.noResults}>
              <img
                src={NoDataIllustration}
                alt="Ilustração de sem resultados"
              />
              <h2>Sem resultados</h2>

              <div className={styles.resetDataButtonWrapper} title="Recarregar">
                <NewButton icon={['fas', 'undo']} onClick={resetData} />
              </div>
            </div>
          )}

        {!isLoading && data?.results?.length && !isDeletingGrammarRule && (
          <div className={styles.tableWrapper}>
            <div className={styles.searchContainer}>
              <Search
                onClick={onSubmitSearch}
                id="name"
                search={{
                  name: 'search',
                  value: searchText,
                  onChange: onChangeSearchText as (
                    eventOrValue: string | React.SyntheticEvent<Element, Event>,
                  ) => void,
                  // TODO: Type onChange correctly on UI
                }}
                searchProps={{
                  placeholder: 'Pesquisar regra',
                  searchTag: 'input',
                  required: false,
                }}
              />

              <div
                data-tip=""
                data-for="numberOfEntitiesPerPageSelect"
                data-testid="numberOfEntitiesPerPageSelectWrapper"
                title="numberOfEntitiesPerPageSelectWrapper"
              >
                <Select
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minWidth: 100,
                      '&:hover': {borderColor: '#5d3d85'},
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      color: '#95989A',
                      paddingRight: 12,
                    }),
                    indicatorSeparator: () => ({}),
                  }}
                  onChange={(event) => setNumberOfGrammarRulesPerPage(event)}
                  options={entitiesPerPageOptions}
                  value={numberOfGrammarRulesPerPage}
                  inputId="entityEvaluationVersion"
                  placeholder="Selecione a versão da regra"
                  name="entityEvaluationVersion"
                />
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th title="Nome">Nome</th>
                  <th title="Categoria">Categoria</th>
                  <th title="Subcategoria">Subcategoria</th>
                  <th title="Comentário">Comentário</th>
                  <th title="Ações">Ações</th>
                </tr>
              </thead>

              <tbody>
                {data?.results?.map((item) => (
                  <tr key={item.id}>
                    <td title={item.name}>{item.name}</td>
                    <td title={item.category}>{item.category}</td>
                    <td title={item.sub_category}>{item.sub_category}</td>
                    <td title={item.comment}>{item.comment}</td>
                    <td>
                      <div className={styles.buttonWrapper}>
                        <Link
                          to={`/regras-gramaticais/${item.id}/${item.latest_version}`}
                          title="Ir aos detalhes da regra"
                        >
                          <Icon
                            icon={['fas', 'external-link']}
                            color="#666"
                            size="lg"
                          />
                        </Link>

                        <button
                          type="button"
                          className={styles.deleteButton}
                          title="Excluir regra"
                          onClick={() => onClickDeleteGrammarRule(item.id)}
                        >
                          <Icon
                            icon={['fas', 'trash-alt']}
                            color="#666"
                            size="lg"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className={styles.paginationWrapper}>
              <Pagination
                currentPage={currentPage}
                hasNext={currentPage < totalNumberOfPages}
                hasPrevious={currentPage > 1}
                totalPages={totalNumberOfPages}
                onChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>
        )}
      </div>

      <DeletionModal
        isOpen={isDeleteGrammarRuleModalOpen}
        message="Deseja mesmo excluir essa regra?"
        onClickDelete={onDeleteGrammarRule}
        onClose={() => setIsDeleteGrammarRuleModalOpen(false)}
        subtitle="Tem certeza?"
        title="Excluir regra"
      />

      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        title="Excluir regra gramatical"
        subtitle={isDeleteError ? 'Erro!' : 'Sucesso!'}
        feedbackMessage={
          isDeleteError
            ? 'Ocorreu um erro ao excluir a regra, tente novamente.'
            : 'A regra foi excluída.'
        }
        onClose={() => setIsFeedbackModalOpen(false)}
        onButtonClick={() => setIsFeedbackModalOpen(false)}
        buttonText={isDeleteError ? 'Tentar novamente' : 'Voltar'}
        iconName={isDeleteError ? 'undo' : 'arrow-left'}
      />
    </PageWrapper>
  );
};

export default GrammarRuleList;
